if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }

      if (this.zoomInitHandler) {
        window.removeEventListener('on:debounced-resize', this.zoomInitHandler);
      }
    }

    init() {
      this.section = this.closest('.js-product');
      this.noSelectedVariant = this.hasAttribute('data-no-selected-variant');
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled')
        && this.getMediaGroupData();
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');

      if (this.hasAttribute('data-zoom-enabled')) {
        this.galleryModal = this.querySelector('.js-media-zoom-template').content.firstElementChild.cloneNode(true);
      }

      if (this.mediaGroupingEnabled && !this.noSelectedVariant) {
        this.setActiveMediaGroup(this.getSelectedMediaGroup());
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
        setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, this.stackedScroll === 'always'), 200);
      } else {
        this.initGallery();
      }

      if (this.zoomLinks) {
        if (this.dataset.zoomTrigger === 'hover') {
          this.triggerZoomInit();
        } else {
          this.zoomLinks.forEach((zoomLink) => {
            zoomLink.addEventListener('click', (evt) => {
              this.triggerZoomInit();
              evt.preventDefault();
            });
          });
        }
      }

      this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
    }

    triggerZoomInit() {
      this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
      this.zoomEventListener = this.zoomEventListener || this.handleZoomMouseMove.bind(this);
      window.addEventListener('on:debounced-resize', this.zoomInitHandler);
      this.initZoom();
      this.zoomLinks.forEach((zoomLink) => {
        zoomLink.addEventListener('click', (evt) => {
          evt.preventDefault();
        });
      });
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
      if (evt.detail && evt.detail.variant) {
        this.selectedVariant = evt.detail.variant;
      }

      if (this.mediaGroupingEnabled) {
        let group = null;
        if (evt.detail && evt.detail.variant) {
          const mediaGroupMap = this.getMediaGroupMap();
          group = mediaGroupMap.groupFromVariant(evt.detail.variant);
        }
        // false (not null) = a variant change with no featured image, so skip the page-load fallback
        const featuredId = evt.detail?.variant?.featured_media?.id || false;
        this.setActiveMediaGroup(group || this.getSelectedMediaGroup(), featuredId);
      }

      if (evt.detail && evt.detail.variant && evt.detail.variant.featured_media) {
        const variantMedia = this.viewer.querySelector(
          `[data-media-id="${evt.detail.variant.featured_media.id}"]`
        );
        if (variantMedia) {
          this.customSetActiveMedia(variantMedia, true);
        }
      }
    }

    /**
     * Gets the media group for the currently selected variant on load or when called.
     * @returns {?object}
     */
    getSelectedMediaGroup() {
      const mediaGroupMap = this.getMediaGroupMap();
      if (!mediaGroupMap || !mediaGroupMap.groups || Object.keys(mediaGroupMap.groups).length === 0) {
        return null;
      }

      // 1. Check if variant-picker has selected variant or script data
      const vp = this.section.querySelector('variant-picker');
      if (vp) {
        if (vp.variant) {
          const group = mediaGroupMap.groupFromVariant(vp.variant);
          if (group) return group;
        }
        const vpScript = vp.querySelector('script[type="application/json"]');
        if (vpScript) {
          try {
            const vData = JSON.parse(vpScript.textContent);
            const group = mediaGroupMap.groupFromVariant(vData);
            if (group) return group;
          } catch (e) {}
        }
      }

      // 2. Check product form variant id input
      const idInput = this.section.querySelector('.js-product-form [name="id"], [name="id"].js-product-form-variant-id');
      if (idInput && idInput.value) {
        const variantId = parseInt(idInput.value, 10);
        const group = Object.values(mediaGroupMap.groups).find((g) => g.variantId === variantId);
        if (group) return group;
      }

      // 3. Check selected option inputs or dropdowns
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      for (let i = 0; i < optionSelectors.length; i += 1) {
        const selector = optionSelectors[i];
        let val = null;
        if (selector.dataset.selectorType === 'dropdown') {
          val = selector.querySelector('.custom-select__btn')?.textContent?.trim();
        } else {
          val = selector.querySelector('input:checked')?.value;
        }
        if (val) {
          if (mediaGroupMap.groups[val]) {
            return mediaGroupMap.groups[val];
          }
          const byTitle = Object.values(mediaGroupMap.groups).find((g) => g.name === val || g.name.split(' / ').includes(val));
          if (byTitle) return byTitle;
        }
      }

      // 4. Check initial is-current-variant element in DOM
      const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
      if (currentItem) {
        const group = mediaGroupMap.groupFromItem(currentItem);
        if (group) return group;
      }

      // 5. Fallback to first group
      return Object.values(mediaGroupMap.groups)[0] || null;
    }

    /**
     * Gets the media group from currently selected variant options.
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors() {
      return this.getSelectedMediaGroup()?.name || null;
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        if (dataEl) {
          try {
            this.variantMediaData = JSON.parse(dataEl.textContent);
          } catch (e) {
            console.error('Error parsing variant media data', e);
            this.variantMediaData = false;
          }
        } else {
          this.variantMediaData = false;
        }
      }

      return this.variantMediaData;
    }

    /**
     * Helper to get the image priority based on featured status and ALT text keywords.
     * 0: Featured Image
     * 1: USP
     * 2: Lifestyle
     * 3: Dimension
     * 4: No keyword
     * 5: Other (ALT tagged "other" always goes last)
     * Keywords match as whole words, so e.g. "usp" inside another word does not count.
     * @param {object|Element} item - Group item object or media element
     * @param {boolean} [isFeatured=false] - Whether this item is the variant featured image
     * @returns {number} Priority index (0 to 5)
     */
    getImagePriority(item, isFeatured = false) {
      if (isFeatured || item.isFeatured) {
        return 0;
      }

      const el = item.main || item;
      const img = el.querySelector ? el.querySelector('img') : null;
      const altText = (item.alt || el.dataset?.alt || img?.alt || el.getAttribute?.('data-alt') || '').trim().toLowerCase();
      const hasWord = (word) => new RegExp(`(^|[^a-z0-9äöüß])${word}([^a-z0-9äöüß]|$)`).test(altText);

      if (!altText) {
        return 4;
      }

      if (hasWord('usp')) {
        return 1;
      }

      if (hasWord('lifestyle')) {
        return 2;
      }

      if (hasWord('dimension')) {
        return 3;
      }

      if (hasWord('other')) {
        return 5;
      }

      return 4;
    }

    /**
     * Stably sorts a variant's sequential range of media items according to priority:
     * Featured (0) -> USP (1) -> Lifestyle (2) -> Dimension (3) -> Other (4)
     * Preserves original relative order for items with equal priority.
     * @param {Array} items - Array of items within the variant's sequential range
     * @returns {Array} Sorted items array
     */
    sortVariantRangeItems(items) {
      if (!items || items.length <= 1) return items;

      return items
        .map((item, originalIndex) => ({
          item,
          originalIndex,
          priority: this.getImagePriority(item, item.isFeatured)
        }))
        .sort((a, b) => {
          if (a.priority !== b.priority) {
            return a.priority - b.priority;
          }
          return a.originalIndex - b.originalIndex;
        })
        .map((entry) => entry.item);
    }

    /**
     * Gets an object mapping media to groups, and the reverse.
     * Uses existing Sequential Range logic as foundation and applies ALT-priority sorting.
     * @returns {?object}
     */
    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = {
          groups: {}
        };

        // set up grouping using existing Sequential Range logic
        const variantMediaData = this.getMediaGroupData();
        if (!variantMediaData || !variantMediaData.variantMedia) {
          return this.mediaGroupMap;
        }

        let currentGroupName = false;
        let currentVariantId = false;
        this.viewerItems = this.querySelectorAll('.media-viewer__item');

        this.viewerItems.forEach((item) => {
          let itemMediaData = null;
          const mediaId = String(item.dataset.mediaId);

          for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
            if (String(variantMediaData.variantMedia[i].mediaId) === mediaId) {
              itemMediaData = variantMediaData.variantMedia[i];
              if (itemMediaData.option && currentGroupName !== itemMediaData.option) {
                currentGroupName = itemMediaData.option;
                currentVariantId = itemMediaData.variantId;
              }
              break;
            }
          }

          if (currentGroupName) {
            const isFeatured = !this.mediaGroupMap.groups[currentGroupName];
            if (!this.mediaGroupMap.groups[currentGroupName]) {
              this.mediaGroupMap.groups[currentGroupName] = {
                name: currentGroupName,
                variantId: currentVariantId,
                items: []
              };
            }
            const groupItem = {
              main: item,
              isFeatured: isFeatured,
              alt: itemMediaData?.alt || item.dataset.alt || ''
            };
            if (this.thumbs) {
              groupItem.thumb = this.thumbs.querySelector(
                `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`
              );
            }
            this.mediaGroupMap.groups[currentGroupName].items.push(groupItem);
          }
        });

        // Apply ALT-priority sorting ONLY inside each variant's sequential range
        Object.keys(this.mediaGroupMap.groups).forEach((groupName) => {
          const group = this.mediaGroupMap.groups[groupName];
          group.items = this.sortVariantRangeItems(group.items);
        });

        // add helper: groupFromItem
        this.mediaGroupMap.groupFromItem = (item) => {
          if (!item) return null;
          const itemId = String(item.dataset?.mediaId || item.main?.dataset?.mediaId || '');
          for (const group of Object.values(this.mediaGroupMap.groups)) {
            for (const grpItem of group.items) {
              if (String(grpItem.main.dataset.mediaId) === itemId) {
                return group;
              }
            }
          }
          return Object.values(this.mediaGroupMap.groups)[0] || null;
        };

        // add helper: groupFromVariant
        this.mediaGroupMap.groupFromVariant = (variant) => {
          if (!variant) return null;
          if (variant.id) {
            const byId = Object.values(this.mediaGroupMap.groups).find((g) => String(g.variantId) === String(variant.id));
            if (byId) return byId;
          }
          if (variant.title && this.mediaGroupMap.groups[variant.title]) {
            return this.mediaGroupMap.groups[variant.title];
          }
          if (variant.options && Array.isArray(variant.options)) {
            for (let i = 0; i < variant.options.length; i += 1) {
              const opt = variant.options[i];
              if (opt && this.mediaGroupMap.groups[opt]) {
                return this.mediaGroupMap.groups[opt];
              }
              const bySub = Object.values(this.mediaGroupMap.groups).find((g) => g.name === opt || g.name.split(' / ').includes(opt));
              if (bySub) return bySub;
            }
          }
          if (variant.featured_media && variant.featured_media.id) {
            const featId = String(variant.featured_media.id);
            for (const group of Object.values(this.mediaGroupMap.groups)) {
              if (group.items.some((it) => String(it.main.dataset.mediaId) === featId)) {
                return group;
              }
            }
          }
          return null;
        };
      }

      return this.mediaGroupMap;
    }

    /**
     * Orders a group's items for display. The selected variant's featured image goes first,
     * then the rest by ALT priority (USP -> Lifestyle -> Dimension -> Other).
     * Falls back to the group's own first image when the variant has no featured image in the group.
     * @param {object} group - Media group
     * @param {?(string|number)} featuredId - Media ID of the selected variant's featured image
     * @returns {Array} Ordered items
     */
    getOrderedGroupItems(group, featuredId) {
      const mediaIdOf = (it) => String(it.main.dataset.mediaId);
      let id = featuredId ? String(featuredId) : null;
      if (!id || !group.items.some((it) => mediaIdOf(it) === id)) {
        id = group.items.length ? mediaIdOf(group.items[0]) : null;
      }

      // Featured images of the other variants (e.g. size M and L) are hidden;
      // only the selected variant's featured image is shown, followed by the shared images.
      const variantFeaturedIds = new Set(
        ((this.getMediaGroupData() || {}).variants || [])
          .map((v) => v.featuredMediaId)
          .filter(Boolean)
          .map(String)
      );

      const items = group.items
        .filter((it) => mediaIdOf(it) === id || !variantFeaturedIds.has(mediaIdOf(it)))
        .map((it) => ({ ...it, isFeatured: mediaIdOf(it) === id }));

      return this.sortVariantRangeItems(this.filterOptionTaggedItems(items));
    }

    /**
     * Returns the option values of the selected variant, lowercased.
     * @returns {Array<string>}
     */
    getSelectedOptionValues() {
      let variant = this.selectedVariant;

      if (!variant) {
        const vp = this.section.querySelector('variant-picker');
        if (vp && vp.variant) {
          variant = vp.variant;
        } else if (vp) {
          const vpScript = vp.querySelector('script[type="application/json"]');
          try {
            variant = vpScript ? JSON.parse(vpScript.textContent) : null;
          } catch (e) {
            variant = null;
          }
        }
      }

      if (!variant) {
        const idInput = this.section.querySelector('.js-product-form [name="id"], [name="id"].js-product-form-variant-id');
        const variants = (this.getMediaGroupData() || {}).variants || [];
        variant = idInput && variants.find((v) => String(v.id) === String(idInput.value));
      }

      return ((variant && variant.options) || [])
        .filter(Boolean)
        .map((v) => String(v).trim().toLowerCase());
    }

    /**
     * Option-tagged USP / Lifestyle / Dimension / Other images, e.g. ALT "S-usp", "M-lifestyle", "L-dimension", "M-other".
     * Checked first, per image type:
     * - Images tagged for the selected option value are shown.
     * - Images tagged for another option value are hidden.
     * - Untagged images of a type are hidden only when the selected option has its own tagged image of that type;
     *   otherwise they stay (the existing range logic is the fallback).
     * @param {Array} items - Group items, already filtered to the selected variant's featured image
     * @returns {Array} Filtered items
     */
    filterOptionTaggedItems(items) {
      const types = ['usp', 'lifestyle', 'dimension', 'other'];
      const typeByPriority = { 1: 'usp', 2: 'lifestyle', 3: 'dimension', 5: 'other' };
      const variants = (this.getMediaGroupData() || {}).variants || [];

      // Every option value on the product, longest first so "XS-usp" never counts as "S-usp"
      const allValues = [...new Set(variants.flatMap((v) => v.options || []).map((v) => String(v).trim().toLowerCase()))]
        .filter(Boolean)
        .sort((a, b) => b.length - a.length);
      if (!allValues.length) return items;

      const escape = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const sep = '[^a-z0-9äöüß]';
      const altOf = (it) => (it.alt || it.main.dataset.alt || '').trim().toLowerCase();

      const tagOf = (it) => {
        const alt = altOf(it);
        for (const type of types) {
          for (const value of allValues) {
            if (new RegExp(`(^|${sep})${escape(value)}\\s*-\\s*${type}(${sep}|$)`).test(alt)) {
              return { type, value };
            }
          }
        }
        return null;
      };

      const typeOf = (it) => typeByPriority[this.getImagePriority(it)] || null;

      const selectedValues = this.getSelectedOptionValues();
      const tagged = items.map((it) => ({ it, tag: it.isFeatured ? null : tagOf(it) }));
      const hasOwnTagged = {};
      tagged.forEach(({ tag }) => {
        if (tag && selectedValues.includes(tag.value)) hasOwnTagged[tag.type] = true;
      });

      return tagged
        .filter(({ it, tag }) => {
          if (it.isFeatured) return true;
          if (tag) return selectedValues.includes(tag.value);
          const type = typeOf(it);
          return !(type && hasOwnTagged[type]);
        })
        .map(({ it }) => it);
    }

    /**
     * Show only images associated to the current variant in prioritized order
     * @param {string|object} groupOrName - optional - Group object or group name to show
     * @param {?(string|number)} featuredId - optional - Media ID of the selected variant's featured image
     */
    setActiveMediaGroup(groupOrName, featuredId = null) {
      const mediaGroupMap = this.getMediaGroupMap();
      if (!mediaGroupMap || !mediaGroupMap.groups || Object.keys(mediaGroupMap.groups).length === 0) {
        return;
      }

      let selectedGroup = null;
      if (typeof groupOrName === 'string') {
        selectedGroup = mediaGroupMap.groups[groupOrName] || Object.values(mediaGroupMap.groups).find((g) => g.name === groupOrName || g.name.includes(groupOrName));
      } else if (groupOrName && typeof groupOrName === 'object' && groupOrName.items) {
        selectedGroup = groupOrName;
      }

      if (!selectedGroup) {
        selectedGroup = this.getSelectedMediaGroup();
      }

      if (selectedGroup) {
        this.currentGroup = selectedGroup;

        // On first load there is no variant event, so use the media Liquid marked as the selected variant's
        const initialFeatured = featuredId === null
          ? this.querySelector('.media-viewer__item.is-current-variant')
          : null;
        const orderedItems = this.getOrderedGroupItems(
          selectedGroup,
          featuredId || initialFeatured?.dataset.mediaId
        );

        this.viewerItems = this.querySelectorAll('.media-viewer__item');
        this.thumbsItems = this.querySelectorAll('.media-thumbs__item');

        // Hide all viewer items and thumbnail items
        this.viewerItems.forEach((item) => {
          item.style.display = 'none';
          item.classList.remove('media-viewer__item--single');
        });
        this.thumbsItems.forEach((item) => {
          item.style.display = 'none';
        });

        // Show and reorder only the selected variant's items
        orderedItems.forEach((item) => {
          this.viewer.appendChild(item.main);
          item.main.style.display = '';
          if (item.thumb) {
            if (this.thumbs) {
              this.thumbs.appendChild(item.thumb);
            }
            item.thumb.style.display = '';
          }
        });

        if (this.galleryModal) {
          const zoomThumbs = this.galleryModal.querySelector('.gallery-zoom__thumbs');
          if (zoomThumbs) {
            orderedItems.forEach((item) => {
              const zoomThumb = zoomThumbs.querySelector(`[data-media-id="${item.main.dataset.mediaId}"]`);
              if (zoomThumb) {
                zoomThumbs.appendChild(zoomThumb);
              }
            });
          }
        }

        this.setVisibleItems();

        if (this.visibleItems.length > 1) {
          this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
        }

        // Always activate the first image (featured image of this variant)
        if (orderedItems.length > 0) {
          this.customSetActiveMedia(orderedItems[0].main, true);
        }

        // Handle single images on stacked view
        if (orderedItems.length === 1) {
          orderedItems[0].main.classList.add('media-viewer__item--single');
        }
      }
    }

    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.setVisibleItems();
      if (this.visibleItems.length <= 1) return;

      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      this.currentItem = this.visibleItems[this.currentIndex];
      this.addListeners();

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`
        );
      }

      if (!this.isFeatured && document.hasFocus()) {
        // Eager load the slider images for smooth UX
        this.viewer.querySelectorAll('.product-image[loading="lazy"]').forEach((img, index) => {
          setTimeout(() => {
            img.loading = 'eager';
          }, 500 * (index + 1));
        });
      }

      const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
      this.customSetActiveMedia(currentItem, true);
    }

    addListeners() {
      this.viewer.addEventListener('scroll', this.handleScroll.bind(this));
      if (this.controls) this.controls.addEventListener('click', this.handleNavClick.bind(this));
      if (this.thumbs) this.thumbs.addEventListener('click', this.handleThumbClick.bind(this));
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Initialized the zoom on hover for desktop
     */
    initZoom() {
      this.zoomLinks.forEach((el) => {
        const zoomWidth = Number(el.querySelector('.zoom-image').dataset.originalWidth || 0);
        const imageWidth = el.querySelector('.product-image').getBoundingClientRect().width;
        if (theme.mediaMatches.md && ((zoomWidth - 75) > (imageWidth))) {
          el.addEventListener('mousemove', this.zoomEventListener);
          el.classList.remove('pointer-events-none');
        } else {
          el.removeEventListener('mousemove', this.zoomEventListener);
          el.classList.add('pointer-events-none');
        }
      });
    }

    /**
     * Handles mouse move over a zoomable image
     * @param {?object} evt - Event object.
     */
    handleZoomMouseMove(evt) {
      if (this.hasAttribute('data-zoom-enabled') && !this.dataset.zoomTrigger && !evt.target.closest('.gallery-zoom-modal')) {
        return;
      }

      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector('.js-zoom-image');

      // Download the zoom image if necessary
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove('loading-spinner--out');

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          hoverElem.classList.remove('media--zoom-not-loaded');
          this.loadingSpinner.classList.add('loading-spinner--out');
        };
        zoomImage.removeAttribute('data-src');
      }

      try {
        const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
        const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;
        zoomImage.style.objectPosition = `${x}% ${y}%`;
      } catch (err) {
        // Fail silently
      }
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);

      if (newIndex !== this.currentIndex) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (!evt.target.matches('.media-ctrl__btn')) return;

      const itemToShow = evt.target === this.nextBtn
        ? this.visibleItems[this.currentIndex + 1]
        : this.visibleItems[this.currentIndex - 1];

      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: 'smooth' });
    }

    /**
     * Handles 'click' events on the thumbnails container.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      const thumb = evt.target.closest('[data-media-id]');
      if (!thumb) return;

      const itemToShow = this.querySelector(`[data-media-id="${thumb.dataset.mediaId}"]`);
      this.customSetActiveMedia(itemToShow);

      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      // Reset distance from leading edge of one slide to the next.
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    /**
     * Stub for variant-picker calls. Listening to change event instead.
     */
    // eslint-disable-next-line class-methods-use-this
    setActiveMedia() {}

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     */
    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (!mediaItem) return;
      if (mediaItem === this.currentItem && !scrollToItem) return;
      window.pauseAllMedia(this);
      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);
      if (this.currentIndex === -1) {
        this.currentIndex = 0;
        this.currentItem = this.visibleItems[0] || mediaItem;
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        // Update the active class and scroll to the active media
        if (this.stackedUnderline) {
          if (this.previousMediaItem) this.previousMediaItem.classList.remove('is-active');
          mediaItem.classList.add('is-active');
          this.previousMediaItem = mediaItem;
        }

        if (this.stackedScroll !== 'never') {
          const y = mediaItem.getBoundingClientRect().top
            + document.documentElement.scrollTop - 150;

          // If the element is far enough away to scroll to it
          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: 'smooth'
            });
          }
        }
        return;
      }

      if (scrollToItem) this.viewer.scrollTo({ left: this.currentItem.offsetLeft });
      if (this.thumbs) this.setActiveThumb();

      if (this.controls) {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentIndex === 0;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
        }

        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex + 1;
        }
      }

      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

      if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }

    /**
     * Sets the active thumbnail.
     */
    setActiveThumb() {
      if (!this.thumbs || !this.currentItem) return;
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem.dataset.mediaId}"]`
      );
      if (!this.currentThumb) return;
      const btn = this.currentThumb.querySelector('button');
      if (!btn) return;

      this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });

      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'true');
      this.checkThumbVisibilty(this.currentThumb);
    }



    /**
     * Creates an array of the visible media items.
     */
    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.visibleItems = Array.from(this.viewerItems).filter((el) => el.clientWidth > 0);
      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    /**
     * Ensures a thumbnail is in the visible area of the slider.
     * @param {Element} thumb - Thumb item element.
     */
    checkThumbVisibilty(thumb) {
      const scrollPos = this.thumbs.scrollLeft;
      const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
      const thumbOffset = thumb.offsetLeft;

      if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
        this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' });
      }
    }

    /**
     * Updates the media gallery status.
     * @param {Element} mediaItem - Active media element.
     * @param {number} index - Active media index.
     */
    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');
      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    /**
     * Loads the deferred media for the active item.
     * @param {Element} mediaItem - Active media element.
     */
    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    }
  }

  customElements.define('media-gallery', MediaGallery);
}
